import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "utils"

const extractTechnicalTicketList = (payload) => {
    if (payload?.status !== 200) return []
    const body = payload?.data
    if (Array.isArray(body?.data)) return body.data
    if (Array.isArray(body?.data?.list)) return body.data.list
    if (Array.isArray(body?.list)) return body.list
    if (Array.isArray(body)) return body
    return []
}

export const technicalTicketList = createAsyncThunk(
    'technicalTicket/list',
    async ({ pageable: data, navigate }) => {
        try {
            const res = await axios({
                method: 'GET',
                url: getConfig.API_URL + 'technical_ticket',
                params: {
                    pageSize: data?.pageSize,
                    pageNo: data?.currentPage,
                    search: data?.search,
                    premise_id: data?.premise_id,
                    ...(data?.incident_status ? { incident_status: data.incident_status } : {}),
                    ...(data?.month ? { month: data.month } : {}),
                    ...(data?.year ? { year: data.year } : {}),
                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: SessionManager.getCookie("user_token"),
                },
            })
            return res
        } catch (error) {
            return error.response
        }
    }
)

export const technicalTicketDetails = createAsyncThunk(
    'technicalTicket/details',
    async ({ id, navigate }) => {
        try {
            const res = await axios({
                method: 'GET',
                url: getConfig.API_URL + `technical_ticket/${id}`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: SessionManager.getCookie("user_token"),
                },
            })
            return res
        } catch (error) {
            return error.response
        }
    }
)

export const addTechnicalTicket = createAsyncThunk(
    'technicalTicket/add',
    async (data) => {
        try {
            const res = await axios({
                method: 'POST',
                url: getConfig.API_URL + 'technical_ticket',
                data: {
                    premise_id: data?.premise_id ?? '',
                    incident_title: data?.incident_title ?? '',
                    admin_remark: data?.admin_remark ?? '',
                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: SessionManager.getCookie('user_token'),
                },
            })
            return res
        } catch (error) {
            return error.response
        }
    }
)

export const updateTechnicalTicket = createAsyncThunk(
    'technicalTicket/update',
    async (data) => {
        try {
            const res = await axios({
                method: 'POST',
                url: getConfig.API_URL + 'update_technical_ticket',
                data: {
                    technical_ticket_id: data?.technical_ticket_id ?? data?.id ?? '',
                    premise_id: data?.premise_id ?? '',
                    incident_title: data?.incident_title ?? '',
                    admin_remark: data?.admin_remark ?? '',
                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: SessionManager.getCookie('user_token'),
                },
            })
            return res
        } catch (error) {
            return error.response
        }
    }
)

export const updateTechnicalTicketStatus = createAsyncThunk(
    'technicalTicket/updateStatus',
    async ({ technical_ticket_id, incident_status, remark }) => {
        try {
            const res = await axios({
                method: 'PUT',
                url: getConfig.API_URL + 'technical_ticket_status',
                data: {
                    technical_ticket_id,
                    incident_status,
                    ...(remark != null && String(remark).trim() !== '' ? { xa_remark: String(remark).trim() } : {}),
                },
                headers: {
                    "Content-Type": "application/json",
                    Authorization: SessionManager.getCookie('user_token'),
                },
            })
            return res
        } catch (error) {
            return error.response
        }
    }
)

export const delTechnicalTicket = createAsyncThunk(
    'technicalTicket/delete',
    async (data) => {
        try {
            const res = await axios({
                method: 'DELETE',
                url: getConfig.API_URL + `technical_ticket/${data?.id}`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: SessionManager.getCookie('user_token'),
                },
            })
            return res
        } catch (error) {
            return error.response
        }
    }
)

const technicalTicket = createSlice({
    name: 'technicalTicket',
    initialState: {
        list: [],
        details: null,
        loading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(technicalTicketList.pending, (state) => {
            state.loading = true
        })
        builder.addCase(technicalTicketList.fulfilled, (state, action) => {
            state.loading = false
            if (action?.payload?.status === 200) {
                state.list = extractTechnicalTicketList(action.payload)
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate)
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.list = []
            }
        })
        builder.addCase(technicalTicketDetails.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.details = action.payload.data.data
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate)
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.details = null
            }
        })
    },
})

export default technicalTicket.reducer
